import { Theme as MuiTheme } from '@mui/material/styles';

export enum PressedKey {
  Space = 'Space',
  Escape = 'Escape',
  Enter = 'Enter',
  Backspace = 'Backspace',
  Left = 'ArrowLeft',
  Right = 'ArrowRight',
}

// TODO: make styled components expect theme as a property instead of using type casting
export type Theme = MuiTheme

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

export enum ThemePreference {
  Light = 'light',
  Dark = 'dark',
  Auto = 'auto',
}

export enum EntityType {
  Note = 'note',
  Reference = 'resource', // TODO: data fix needed to rename to reference
  Query = 'query',
  Chart = 'chart',
  Grid = 'grid',
  Anchor = 'anchor',
  AnchorEffectAssignValue = 'anchorEffectAssignValue',
  Timeline = 'timeline',
  TimelineCollection = 'timelineCollection',
  Repository = 'repository',
  RepositoryEntry = 'repositoryEntry',
  PropertyDefinitionBoolean = 'templatePropertyBoolean',
  PropertyDefinitionText = 'templatePropertyText',
  PropertyDefinitionString = 'templatePropertyString',
  PropertyDefinitionSelectString = 'templatePropertySelectString',
  PropertyDefinitionSelectStringOption = 'templatePropertySelectStringOption',
  PropertyDefinitionBelongsTo = 'templatePropertyBelongsTo',
  // PropertyDefinitionSelectBelongsTo = 'templatePropertySelectBelongsTo',
  // PropertyDefinitionSelectBelongsToOption = 'templatePropertySelectBelongsToOption',
  PropertyDefinitionHas = 'templatePropertyHas',
  PropertyDefinitionDate = 'templatePropertyDate',
  PropertyDefinitionDatetime = 'templatePropertyDatetime',
  PropertyDefinitionNumber = 'templatePropertyNumber',
  EntryPropertyString = 'entryPropertyString',
  EntryPropertyBoolean = 'entryPropertyBoolean',
  EntryPropertyText = 'entryPropertyText',
  EntryPropertyBelongsTo = 'entryPropertyBelongsTo',
  EntryPropertyDate = 'entryPropertyDate',
  EntryPropertyDatetime = 'entryPropertyDatetime',
  EntryPropertyNumber = 'entryPropertyNumber',
}

export interface CanvasPosition {
  x: number;
  y: number;
}

export type NoteVariant = 'default' | 'long' | 'header';

interface NoteModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  title: string,
  description: string,
  /** Canvas presentation: `long` is wide paragraph layout; `header` is label-only with zoom-compensated typography when zoomed out. */
  variant?: NoteVariant,
  /** Note surface color (bookmark palette hex). */
  color?: string | null,
  xy?: CanvasPosition,
  /** ID of the AnchorEntry this canvas card is pinned to, or null if free. */
  anchorId?: string | null,
}

interface ReferenceModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  repositoryId: string,
  repositoryEntryId: string,
  xy?: CanvasPosition,
  /** ID of the AnchorEntry this canvas card is pinned to, or null if free. */
  anchorId?: string | null,
}

interface QueryModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  repositoryId: string,
  title: string,
  where?: string | null,
  includeArchived?: boolean,
  xy?: CanvasPosition,
}

interface ChartModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  repositoryId: string,
  title: string,
  where?: string | null,
  includeArchived?: boolean,
  metric?: string,
  xAxis?: string,
  /** Day / week / month bucketing when x-axis is a date or datetime field. */
  xAxisTimeScale?: 'D' | 'W' | 'M' | null,
  /** Line chart (default), bar chart, or GitHub-style daily activity heatmap (date/datetime x-axis only). */
  variant?: 'line' | 'bar' | 'heatmap',
  /** Optional reference level(s) for line/bar (metric units). */
  targetA?: number | null,
  targetB?: number | null,
  xy?: CanvasPosition,
}

interface GridModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  repositoryId?: string | null,
  /** Axis grouping: belongs-to / has property on the collection (target repo defines header references). */
  primaryPropertyId?: string | null,
  secondaryPropertyId?: string | null,
  /** `vertical`: swimlanes on X; `horizontal`: swimlanes on Y. */
  direction?: string | null,
  xy?: CanvasPosition,
}

interface AnchorModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  /** Collection whose entries can be snapped onto the anchor line. */
  repositoryId?: string | null,
  /** Canvas reference entry this anchor is bound to. Null for a free-standing anchor. */
  referenceId?: string | null,
  /** `vertical`: line projects downward; `horizontal`: line projects rightward. */
  direction?: string | null,
  xy?: CanvasPosition,
}

/** A hook configured on an anchor: when something attaches to `anchorId`, assign `value` to `propertyId` on the attached entry. */
interface AnchorEffectAssignValueModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  anchorId: string,
  repositoryId: string,
  propertyId: string,
  value: string,
}

interface TimelineModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  name: string,
  xy?: CanvasPosition,
}

/** One collection plotted on a timeline, laid out along the day axis by `datePropertyId`. */
interface TimelineCollectionModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  timelineId: string,
  repositoryId: string,
  /** Date or datetime property on the collection used as the day axis. */
  datePropertyId: string,
}

interface RepositoryModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string | null,
  title: string,
  singular: string | null,
  rank?: number,
  color?: string | null,
  isBookmarked?: boolean,
}

export interface PropertyDefinitionConfig {
	id: string;
	singular: string;
	summary?: string;
}

interface PropertyDefinitionSelectStringModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
}

interface PropertyDefinitionSelectStringOptionModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  value: string,
}

interface PropertyDefinitionStringModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  placeholder?: string,
}

interface PropertyDefinitionBooleanModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
  defaultChecked?: boolean,
}

interface PropertyDefinitionTextModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required: boolean,
}

interface PropertyDefinitionBelongsToModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  targetRepositoryId: string,
  required: boolean,
  allowMany: boolean,
}

interface PropertyDefinitionHasModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  targetRepositoryId: string,
  targetPropertySlug: string,
  required: boolean,
}

interface PropertyDefinitionDateModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  placeholder?: string,
  defaultValue?: string,
}

interface PropertyDefinitionDatetimeModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  placeholder?: string,
  defaultValue?: string,
}

interface PropertyDefinitionNumberModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  label: string,
  slug: string,
  required?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  placeholder?: string,
  defaultValue?: string,
}

interface RepositoryEntryModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  isArchived?: boolean,
  rank?: number | string,
}

interface EntryPropertyStringModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface EntryPropertyTextModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface EntryPropertyBooleanModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: boolean,
  isArchived?: boolean,
}

interface EntryPropertyBelongsToModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  targetRepositoryId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface EntryPropertyDateModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface EntryPropertyDatetimeModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface EntryPropertyNumberModel {
  id: string,
  fakeId?: string,
  companyId?: string,
  parentId: string,
  repositoryId: string,
  propertyDefinitionId: string,
  slug: string,
  value: string,
  isArchived?: boolean,
}

interface ModelPropsMap {
  [EntityType.Note]: NoteModel
  [EntityType.Reference]: ReferenceModel
  [EntityType.Query]: QueryModel,
  [EntityType.Chart]: ChartModel,
  [EntityType.Grid]: GridModel,
  [EntityType.Anchor]: AnchorModel,
  [EntityType.AnchorEffectAssignValue]: AnchorEffectAssignValueModel,
  [EntityType.Timeline]: TimelineModel,
  [EntityType.TimelineCollection]: TimelineCollectionModel,
  [EntityType.Repository]: RepositoryModel,
  [EntityType.RepositoryEntry]: RepositoryEntryModel,
  [EntityType.PropertyDefinitionString]: PropertyDefinitionStringModel,
  [EntityType.PropertyDefinitionBoolean]: PropertyDefinitionBooleanModel,
  [EntityType.PropertyDefinitionText]: PropertyDefinitionTextModel,
  [EntityType.PropertyDefinitionBelongsTo]: PropertyDefinitionBelongsToModel,
  [EntityType.PropertyDefinitionHas]: PropertyDefinitionHasModel,
  [EntityType.PropertyDefinitionSelectString]: PropertyDefinitionSelectStringModel,
  [EntityType.PropertyDefinitionSelectStringOption]: PropertyDefinitionSelectStringOptionModel,
  [EntityType.PropertyDefinitionDate]: PropertyDefinitionDateModel,
  [EntityType.PropertyDefinitionDatetime]: PropertyDefinitionDatetimeModel,
  [EntityType.PropertyDefinitionNumber]: PropertyDefinitionNumberModel,
  [EntityType.EntryPropertyString]: EntryPropertyStringModel,
  [EntityType.EntryPropertyText]: EntryPropertyTextModel,
  [EntityType.EntryPropertyBoolean]: EntryPropertyBooleanModel,
  [EntityType.EntryPropertyBelongsTo]: EntryPropertyBelongsToModel
  [EntityType.EntryPropertyDate]: EntryPropertyDateModel
  [EntityType.EntryPropertyDatetime]: EntryPropertyDatetimeModel
  [EntityType.EntryPropertyNumber]: EntryPropertyNumberModel
}

interface TemplateModelPropsMap {
  [EntityType.PropertyDefinitionString]: PropertyDefinitionStringModel,
  [EntityType.PropertyDefinitionBoolean]: PropertyDefinitionBooleanModel,
  [EntityType.PropertyDefinitionText]: PropertyDefinitionTextModel,
  [EntityType.PropertyDefinitionBelongsTo]: PropertyDefinitionBelongsToModel,
  [EntityType.PropertyDefinitionHas]: PropertyDefinitionHasModel,
  [EntityType.PropertyDefinitionSelectString]: PropertyDefinitionSelectStringModel,
  [EntityType.PropertyDefinitionDate]: PropertyDefinitionDateModel,
  [EntityType.PropertyDefinitionDatetime]: PropertyDefinitionDatetimeModel,
  [EntityType.PropertyDefinitionNumber]: PropertyDefinitionNumberModel,
}

type TemplateProps<T extends keyof TemplateModelPropsMap> = Omit<TemplateModelPropsMap[T] & { type: T }, 'id' | 'parentId'>;

type EntryBaseProps<T extends keyof ModelPropsMap> = ModelPropsMap[T] & { type: T }

export interface EntryUIProps {
  /** Repository entries use fractional-index strings; notes/repositories may still use numbers. */
  rank: number | string,
  /** Local-only flag for optimistic entries not yet synced with server UUIDs. */
  isDraft?: boolean,
}

export type EntryProps<T extends keyof ModelPropsMap> = EntryBaseProps<T> & EntryUIProps;

type ModelTemplatesMap = {
  [K in keyof TemplateModelPropsMap]: TemplateProps<K>
};

type EntriesMap = {
  [K in keyof ModelPropsMap]: EntryProps<K>
};

export type ModelTemplate = ModelTemplatesMap[keyof ModelTemplatesMap];

export type Entry = EntriesMap[keyof EntriesMap];

export type ReferenceEntry = EntryProps<EntityType.Reference>;

export type NoteEntry = EntryProps<EntityType.Note>;

export type QueryEntry = EntryProps<EntityType.Query>;

export type ChartEntry = EntryProps<EntityType.Chart>;

export type GridEntry = EntryProps<EntityType.Grid>;

export type AnchorEntry = EntryProps<EntityType.Anchor>;

export type AnchorEffectAssignValueEntry = EntryProps<EntityType.AnchorEffectAssignValue>;

export type TimelineEntry = EntryProps<EntityType.Timeline>;

export type TimelineCollectionEntry = EntryProps<EntityType.TimelineCollection>;

export type AnyPropertyDefinitionEntityType = EntityType.PropertyDefinitionString | EntityType.PropertyDefinitionBoolean | EntityType.PropertyDefinitionText | EntityType.PropertyDefinitionBelongsTo | EntityType.PropertyDefinitionHas | EntityType.PropertyDefinitionDate | EntityType.PropertyDefinitionDatetime | EntityType.PropertyDefinitionNumber;

export type AnyEntryPropertyEntityType = EntityType.EntryPropertyString | EntityType.EntryPropertyText | EntityType.EntryPropertyBoolean | EntityType.EntryPropertyBelongsTo | EntityType.EntryPropertyDate | EntityType.EntryPropertyDatetime | EntityType.EntryPropertyNumber;

export type PropertyDefinitionStringEntry = EntryBaseProps<EntityType.PropertyDefinitionString>;

export type PropertyDefinitionSelectStringOptionEntry = EntryBaseProps<EntityType.PropertyDefinitionSelectStringOption>;

export type Repository = EntryBaseProps<EntityType.Repository>;

export type RepositoryEntry = EntryProps<EntityType.RepositoryEntry>;

export type PropertyDefinition = EntryBaseProps<AnyPropertyDefinitionEntityType>; // EntryProps (to have the rank)?

export type PropertyDefinitionBoolean = EntryBaseProps<EntityType.PropertyDefinitionBoolean>;

export type PropertyDefinitionBelongsTo = EntryBaseProps<EntityType.PropertyDefinitionBelongsTo>;

export type PropertyDefinitionHas = EntryBaseProps<EntityType.PropertyDefinitionHas>;

export type PropertyDefinitionDate = EntryBaseProps<EntityType.PropertyDefinitionDate>;

export type EntryProperty = EntryBaseProps<AnyEntryPropertyEntityType>;

export type EntryPropertyString = EntryBaseProps<EntityType.EntryPropertyString>;

export type EntryPropertyText = EntryBaseProps<EntityType.EntryPropertyText>;

export type EntryPropertyBelongsTo = EntryBaseProps<EntityType.EntryPropertyBelongsTo>;

export interface SelectOption {
  value: string,
  label: string,
}

export interface BelongsToPopulatedValue {
  id: string, // belongsTo record id
  value: string, // target entry id
  label: string, // target entry label
}

/** Scalar fields use string/boolean; BelongsTo / Has slugs are always `BelongsToPopulatedValue[]` (possibly empty). */
export type PopulatedEntryProperty = string | boolean | BelongsToPopulatedValue[];

export type RepositoryEntryData = {
  [key: string]: PopulatedEntryProperty;
}

// FIXME: this duplicates RepositoryEntriesRowDto. TODO: go over all occurrences of PopulatedEntry, make sure this type makes sense, and then just stick with one type.
export type PopulatedEntry = {
  id: string;
	label: string;
	data: Record<string, unknown>;
	rank: number | string;
	isArchived: boolean;
}

/** Response body for authenticated `GET repositories/:id`. */
export interface RepositoryByIdResponse {
	repository: Repository;
	propertyDefinitions: PropertyDefinition[];
}

export type FormSelectOptions = Record<string, SelectOption[]>;

export type FormData = {
  [key: string]: any;
} & {
  rank?: number;
} & {
  type?: EntityType;
}
